import { compactVerify, createRemoteJWKSet, decodeJwt, errors } from 'jose';
import { withForwardedEnv } from '../config/forwardedEnv';

import {
  ExpiredContextTokenError,
  InvalidContextToken,
  NoContextTokenError,
  type UnexpectedError,
} from './errors';

type SparseMap<K extends string | number | symbol, V> = Partial<Record<K, V>>;

enum CustomerName {
  TRELLO = 'trello',
  ECOSYSTEM = 'ecosystem',
  JIRA = 'jira',
  CONFLUENCE = 'confluence',
  AI = 'ai',
  KAIZEN = 'kaizen',
  PARTNERCENTRAL = 'partnercentral',
  BITBUCKET = 'bitbucket',
  IDENTITY = 'identity',
}

enum ProfileName {
  APPLINKS_OAUTH_2 = 'applinksOAuth2',
  APPLINKS = 'applinks',
  INTEGRATIONS3LO = 'integrations3LO',
  POWERUPS3LO = 'powerups3LO',
  DCR3P = 'dcr3p',
  AI_A2ADCR3P = 'a2adcr3p',
  MCP = 'mcp',
  A2A_DCR3P = 'a2adcr3p',
  ROVO_CLI = 'rovoCLI',
  ROVO_DESKTOP = 'rovoDesktop',
  TWG_CLI = 'twgCLI',
  ECOSYSTEM = 'ecosystem',
  DEFAULT = 'default',
  DEFAULT3LO = 'default3LO',
  LEGACY_OAUTH_PUBLIC = 'legacyOAuthPublic',
  LEGACY_OAUTH_PRIVATE = 'legacyOAuthPrivate',
  DAG_ACTIVATE = 'dagActivate',
}

const customerProfileMap: SparseMap<
  CustomerName,
  SparseMap<ProfileName, ServiceType>
> = {
  [CustomerName.TRELLO]: {
    [ProfileName.INTEGRATIONS3LO]: 'Trello',
    [ProfileName.POWERUPS3LO]: 'Trello',
    [ProfileName.DEFAULT]: 'Trello',
  },
  [CustomerName.ECOSYSTEM]: {
    [ProfileName.ECOSYSTEM]: 'Ecosystem',
    [ProfileName.DEFAULT]: 'Ecosystem',
  },
  [CustomerName.JIRA]: {
    [ProfileName.APPLINKS_OAUTH_2]: 'Applinks',
    [ProfileName.APPLINKS]: 'Applinks',
    [ProfileName.DEFAULT]: 'Ecosystem',
  },
  [CustomerName.CONFLUENCE]: {
    [ProfileName.APPLINKS_OAUTH_2]: 'Applinks',
    [ProfileName.APPLINKS]: 'Applinks',
    [ProfileName.DEFAULT]: 'Ecosystem',
  },
  [CustomerName.AI]: {
    [ProfileName.DCR3P]: 'MCP',
    [ProfileName.MCP]: 'MCP',
    [ProfileName.A2A_DCR3P]: 'A2A',
    [ProfileName.ROVO_CLI]: 'Rovo',
    [ProfileName.ROVO_DESKTOP]: 'Rovo',
    [ProfileName.TWG_CLI]: 'TWG',
    [ProfileName.DEFAULT]: 'MCP',
  },
  [CustomerName.KAIZEN]: {
    [ProfileName.DCR3P]: 'Kaizen',
    [ProfileName.DEFAULT]: 'Kaizen',
  },
  [CustomerName.PARTNERCENTRAL]: {
    [ProfileName.DEFAULT3LO]: 'PartnerCentral',
    [ProfileName.DEFAULT]: 'PartnerCentral',
  },
  [CustomerName.BITBUCKET]: {
    [ProfileName.LEGACY_OAUTH_PUBLIC]: 'Bitbucket',
    [ProfileName.LEGACY_OAUTH_PRIVATE]: 'Bitbucket',
  },
  [CustomerName.IDENTITY]: {
    [ProfileName.DAG_ACTIVATE]: 'DAG',
  },
};

/**
 * Information parsed from the Consent Context JWT
 */
export type ConsentToken = {
  audience: string;
  profile: string | null;
  customer: string | null;
  raw: string;
  issuedAt?: number;
  serviceType: ServiceType;
  scopes: string[];
  userId: string;
  clientId: string | null;
  clientName: string | null;
  clientLogoUrl?: string;
  redirectUrl: RedirectUrl | null;
  cloudId: string | null;
  resources?: string[];
  workspaces?: string[];
};

export type ServiceType =
  | 'OIDC'
  | 'Trello'
  | 'Applinks'
  | 'Ecosystem'
  | 'Kaizen'
  | 'MCP'
  | 'PartnerCentral'
  | 'Bitbucket'
  | 'Rovo'
  | 'TWG'
  | 'A2A'
  | 'DAG';

export type RedirectUrl = {
  raw: string;
  audience: string;
  scopes: string[];
  csrfToken: string;
  callback: string;
};

/**
 * JSON fields in Consent Context JWT passed in via URL params
 */
export type RawConsentToken = {
  aud: string;
  iat?: number;
  profile?: string;
  customer?: string;
  user_id: string;
  scopes?: string[];
  redirect_url: string;
  client?: {
    client_id: string;
    name: string;
    logo_uri?: string;
  };
  cloud_id?: string;
  resources?: string[];
  workspaces?: string[];
};

let JWKS: ReturnType<typeof createRemoteJWKSet>;

export type GetConsentContextOptions = {
  searchParams?: string;
  origin?: string;
  expectedIssuer?: string;
  expectedAudience?: string;
};

/**
 * Get the OIDC Consent Context from the `context` URL query parameter
 */
export async function getConsentContext(
  options?: GetConsentContextOptions,
): Promise<ConsentToken | UnexpectedError> {
  JWKS =
    JWKS ??
    createRemoteJWKSet(
      new URL(
        withForwardedEnv(
          `${options?.origin ?? window.location.origin}/.well-known/jwks.json`,
          options?.searchParams,
        ),
      ),
    );
  const searchParams = new URLSearchParams(
    options?.searchParams ?? window.location.search,
  );
  const contextParam = searchParams.get('context');
  if (!contextParam) {
    return NoContextTokenError;
  }
  let payload;
  try {
    payload = await verifyConsentContextToken(contextParam, {
      issuer: options?.expectedIssuer ?? window.location.host,
      audience: options?.expectedAudience ?? window.location.host,
    });
  } catch (e) {
    console.error('Failed to verify the token', e);
    if (e instanceof errors.JOSEError) {
      if (e instanceof errors.JWTExpired) {
        return ExpiredContextTokenError;
      }
      return InvalidContextToken;
    }
    console.error(
      'Exception thrown during token verification was unexpected',
      e,
    );
    throw e;
  }
  const redirectUrl = parseRedirectURL(payload.redirect_url);
  const serviceType = getServiceType(payload.profile, payload.customer);
  return {
    audience: payload.aud,
    profile: payload.profile || null,
    customer: payload.customer || null,
    raw: contextParam,
    issuedAt: payload.iat,
    serviceType,
    scopes:
      payload.scopes && payload.scopes.length > 0
        ? payload.scopes
        : (redirectUrl?.scopes ?? []),
    userId: payload.user_id,
    clientId: payload.client?.client_id || null,
    clientName: payload.client?.name || null,
    clientLogoUrl: payload.client?.logo_uri,
    redirectUrl,
    cloudId: payload.cloud_id || null,
    resources: payload.resources,
    workspaces: payload.workspaces,
  };
}
const CLOCK_TOLERANCE_SECONDS = 60;

type VerifyConsentContextOptions = {
  issuer: string;
  audience: string;
};

async function verifyConsentContextToken(
  token: string,
  options: VerifyConsentContextOptions,
): Promise<RawConsentToken> {
  await compactVerify(token, JWKS);

  const payload = decodeJwt<RawConsentToken>(token);
  const now = Math.floor(Date.now() / 1_000);

  if (payload.iss !== options.issuer) {
    throw new errors.JWTClaimValidationFailed(
      'unexpected "iss" claim value',
      payload,
      'iss',
      'check_failed',
    );
  }

  const audiences = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
  if (!audiences.includes(options.audience)) {
    throw new errors.JWTClaimValidationFailed(
      'unexpected "aud" claim value',
      payload,
      'aud',
      'check_failed',
    );
  }

  if (
    typeof payload.exp === 'number' &&
    payload.exp <= now - CLOCK_TOLERANCE_SECONDS
  ) {
    throw new errors.JWTExpired(
      '"exp" claim timestamp check failed',
      payload,
      'exp',
      'check_failed',
    );
  }

  return payload;
}

const parseRedirectURL = (uri: string): RedirectUrl | null => {
  if (!uri) return null;
  try {
    const url = new URL('a://a' + uri);
    return {
      raw: uri,
      audience: url.searchParams.get('audience') ?? '',
      scopes: url.searchParams.get('scope')?.split(' ').filter(Boolean) ?? [],
      csrfToken: url.searchParams.get('csrf_token') ?? '',
      callback: url.searchParams.get('redirect_uri') ?? '',
    };
  } catch (e) {
    console.warn(`Could not parse redirect URI: ${uri}`);
    return null;
  }
};

function getServiceType(profile?: string, customerName?: string): ServiceType {
  const c = Object.values(CustomerName).includes(customerName as CustomerName)
    ? (customerName as CustomerName)
    : CustomerName.ECOSYSTEM;
  const p = Object.values(ProfileName).includes(profile as ProfileName)
    ? (profile as ProfileName)
    : ProfileName.DEFAULT;

  // @ts-ignore
  return customerProfileMap[c][p];
}
